import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useCmsPage, c } from '../lib/cms';

const DEFAULT_ECOSYSTEM = [
  { name: 'Xentrios', color: '#2D3250', desc: 'Cloud-native dental PMS with AI workforce support. The clinical and operational foundation.' },
  { name: 'National Data Solutions', color: '#1976d2', desc: 'Data platform, analytics, managed IT, and practice integration. The data and infrastructure layer.' },
  { name: 'RevTech Partners', color: '#388e3c', desc: 'RCM intelligence products and consulting. The revenue recovery and operational intelligence layer.' },
];

export default function AboutPage() {
  const cmsPage = useCmsPage('xentrios', 'about');
  const heroHeadline = c(cmsPage, 'hero', 'headline', "Built by people who understand\ndental operations.");
  const heroSubtitle = c(cmsPage, 'hero', 'subtitle', 'Xentrios was created because every PMS on the market was designed for a single practice and retrofitted for DSOs. We started from scratch — building what multi-location dental organizations actually need.');
  const storyLeftBody = c(cmsPage, 'story', 'body_left', null);
  const storyRightBody = c(cmsPage, 'story', 'body_right', null);
  const ecosystem = c(cmsPage, 'ecosystem', 'items', DEFAULT_ECOSYSTEM);
  return (
    <div>
      <Helmet><title>About — Xentrios</title></Helmet>

      <section className="xen-hero" style={{ padding: '4rem 0 3rem' }}>
        <div className="xen-container">
          <p className="xen-hero-eyebrow">About Xentrios</p>
          <h1>{heroHeadline}</h1>
          <p className="subtitle">{heroSubtitle}</p>
        </div>
      </section>

      <section className="xen-section">
        <div className="xen-container xen-two-col">
          <div>
            <h2>Why we built Xentrios</h2>
            <p style={{ color: '#555', lineHeight: 1.7, marginBottom: '1rem' }}>
              After years of deploying data platforms and RCM tools for DSOs, we kept hitting the same wall:
              the practice management system. Every integration was a workaround. Every report required a data
              export. Every new practice acquisition meant another migration nightmare.
            </p>
            <p style={{ color: '#555', lineHeight: 1.7 }}>
              We built Xentrios to eliminate that wall. A PMS that's natively integrated with the data platform,
              the analytics suite, and the RCM tools — so everything works together from day one, not after
              months of custom development.
            </p>
          </div>
          <div>
            <h2>Our approach to AI</h2>
            <p style={{ color: '#555', lineHeight: 1.7, marginBottom: '1rem' }}>
              AI in healthcare is controversial for good reason. Too many products use AI to make decisions
              behind the scenes — and when something goes wrong, nobody knows why.
            </p>
            <p style={{ color: '#555', lineHeight: 1.7 }}>
              Xentrios takes a different approach. Our AI is designed to <strong>inform and orient</strong> your team,
              not replace their judgment. Every AI suggestion is visible, explainable, and requires human approval.
              The goal is to reduce the administrative burden on clinical staff so they can focus on patient care —
              not to automate decisions that should be made by people.
            </p>
          </div>
        </div>
      </section>

      <section className="xen-section xen-section-light">
        <div className="xen-container xen-section-center">
          <p className="xen-section-eyebrow">Ecosystem</p>
          <h2>Part of the NDS + RevTech ecosystem</h2>
          <p className="xen-section-subtitle" style={{ marginBottom: '2.5rem' }}>
            Xentrios is a strategic partnership between three organizations that together deliver the
            complete technology stack for dental support organizations.
          </p>
          <div className="xen-features">
            {ecosystem.map(e => (
              <div key={e.name} className="xen-feature" style={{ borderTop: `3px solid ${e.color}` }}>
                <h3 style={{ color: e.color }}>{e.name}</h3>
                <p>{e.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="xen-section xen-section-dark" style={{ textAlign: 'center', padding: '4rem 0' }}>
        <div className="xen-container">
          <h2 style={{ color: '#fff' }}>Let's talk.</h2>
          <p style={{ color: 'var(--xen-gray)', maxWidth: 450, margin: '0 auto 2rem' }}>
            See how Xentrios fits your organization. No pressure, no pitch — just an honest conversation.
          </p>
          <Link to="/demo" className="xen-btn xen-btn-primary" style={{ padding: '0.85rem 2.5rem', fontSize: '1rem' }}>Request a Demo</Link>
        </div>
      </section>
    </div>
  );
}
